import {useState} from 'react';

import {usdFormatter} from '@/lib/helpers';
import {SupabaseInstance} from '@/lib/utils';

import {useNotifications} from '@/providers/components/notificationsProvider';

const QuickActions = ({navigate}: {navigate: (path: string) => void}) => {
  const {toast} = useNotifications();

  // State
  const [showModal, setShowModal] = useState(false);
  const [amount, setAmount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const quickActionItems = [
    {
      icon: 'ri-add-circle-line',
      title: 'New Position',
      description: 'Create buy/sell position',
      bgColor: 'bg-primary/10',
      textColor: 'text-primary',
      onClick: () => navigate('/speculator/positions'),
    },
    {
      icon: 'ri-money-dollar-circle-line',
      title: 'Deposit Funds',
      description: 'Add money to account',
      bgColor: 'bg-success/10',
      textColor: 'text-success',
      onClick: () => setShowModal(true),
    },
    {
      icon: 'ri-file-chart-line',
      title: 'Market Analysis',
      description: 'View Insights',
      bgColor: 'bg-warning/10',
      textColor: 'text-warning',
      // onClick: () => {},
    },
    {
      icon: 'ri-customer-service-2-line',
      title: 'Support',
      description: 'Get help',
      bgColor: 'bg-info/10',
      textColor: 'text-info',
      // onClick: () => {},
    },
  ];

  const handlePurchase = async () => {
    setIsLoading(true);
    if (amount > 10000) {
      setIsLoading(false);
      toast.error('Amount must be less than 10,000');
      return;
    }
    try {
      const {data, error} = await SupabaseInstance.supabase.functions.invoke('stripe-checkout-session', {
        body: {amount},
      });

      if (error) {
        console.error('Error creating Stripe checkout session', error);
        toast.error('Failed to create checkout session');
      } else {
        if (data.url) {
          window.location.href = data.url;
        }
      }
    } catch (error) {
      console.error('Error creating Stripe checkout session', error);
      toast.error('Failed to create checkout session');
    } finally {
      setIsLoading(false);
      setShowModal(false);
      setAmount(0);
    }
  };

  return (
    <>
      <div className="grid grid-cols-12 gap-4">
        {quickActionItems.map((item, index) => (
          <div key={index} className="col-span-3 h-full">
            <div className="box cursor-pointer transition-shadow hover:shadow-lg" onClick={item.onClick}>
              <div className="box-body">
                <div className="flex items-center">
                  <div className={`avatar ${item.bgColor} ${item.textColor} me-4`}>
                    <i className={`${item.icon} text-2xl`}></i>
                  </div>
                  <div>
                    <h6 className="mb-1 font-semibold">{item.title}</h6>
                    <p className="mb-0 text-sm text-[#8c9097] dark:text-white/50">{item.description}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Deposit Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-white p-6 dark:bg-gray-800">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-xl font-semibold">Deposit Funds</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="mb-4">
              <div className="flex items-center justify-between text-[#8c9097] dark:text-white/50">
                <div>All Amounts In USD</div>
                <div>$10,000 limit</div>
              </div>
            </div>

            <div className="mb-4">
              <input
                type="text"
                className="form-control w-full"
                placeholder="Enter Amount"
                value={amount}
                onChange={e => setAmount(Number(e.target.value))}
              />
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-[.875rem]">
                <span>Amount:</span>
                <span>{usdFormatter.format(amount)}</span>
              </div>
              <div className="flex justify-between text-[.875rem]">
                <span>TNC fee (2%):</span>
                <span>{usdFormatter.format(amount * 0.02)}</span>
              </div>
              <div className="flex justify-between text-[.875rem]">
                <span>Total:</span>
                <span>{usdFormatter.format(amount + amount * 0.02)}</span>
              </div>
            </div>

            <div className="mt-6 flex gap-2">
              <button onClick={() => setShowModal(false)} className="ti-btn ti-btn-danger-full ti-btn-lg flex-1">
                Cancel
              </button>
              <button
                onClick={handlePurchase}
                disabled={amount <= 0 || isLoading}
                className="ti-btn ti-btn-success-full ti-btn-lg flex-1"
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <i className="ri-loader-4-line animate-spin"></i>
                    Processing...
                  </span>
                ) : (
                  'Purchase'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default QuickActions;
